import { clone } from '@deltasierra/utilities-object';
import { IEllipse, IPoint, IRectangle, ISize } from '../geometry';
import { BlendMode } from '../builderDocument';
import { BuilderFontCustomDto } from '../builderFont';
import {
    FontUnits,
    IImageOptions,
    IRenderContext,
    IRenderContextOptions,
    IRenderContextTextOptions,
    LineCap,
    RenderContextColor,
    TextBaseline,
} from './renderContext';
import { Pixels } from './units';
import { colorSchemeHelpers, convertToScheme } from './colors';
import { DomTextMeasurer, canvasFontToString, drawLine, runInContextTransaction } from './canvasUtils';
import { CanvasFontWeight } from './canvasFont';
import { calculateLineHeightInPixels, findMatchingFontMetricsOrDefault } from './text';

interface ManuallyTrackedOptions {
    fontFamily: string;
    fontSize: number;
    fontUnits: FontUnits;
    fontWeight: CanvasFontWeight;
}

function isCanvas(canvasOrContext: CanvasRenderingContext2D | HTMLCanvasElement): canvasOrContext is HTMLCanvasElement {
    try {
        return canvasOrContext instanceof HTMLCanvasElement;
    } catch (err) {
        if (err.name === 'ReferenceError') {
            return false;
        } else {
            throw err;
        }
    }
}

export class CanvasRenderContext implements IRenderContext {
    private readonly context: CanvasRenderingContext2D;

    private readonly baseline: TextBaseline;

    private readonly textMeasurer: DomTextMeasurer;

    private manuallyTrackedOptionHistory: ManuallyTrackedOptions[] = [];

    private manuallyTrackedOptions: ManuallyTrackedOptions = {
        fontFamily: 'sans-serif',
        fontSize: 10,
        fontUnits: 'px',
        fontWeight: 'normal',
    };

    public constructor(
        canvasOrContext: CanvasRenderingContext2D | HTMLCanvasElement,
        private readonly customFonts: BuilderFontCustomDto[],
        options: IRenderContextOptions,
    ) {
        this.textMeasurer = options.textMeasurer;

        this.baseline = 'top';
        if (options.useTextRenderingLineHeightFix) {
            this.baseline = 'alphabetic';
        } else if (options.useOldTextRenderingLineHeightFix) {
            this.baseline = 'middle';
        }

        if (isCanvas(canvasOrContext)) {
            this.context = canvasOrContext.getContext('2d')!;
        } else {
            this.context = canvasOrContext;
        }
    }

    // Dimensions
    public getSize(): ISize {
        return {
            height: this.context.canvas.height,
            width: this.context.canvas.width,
        };
    }

    public save(): this {
        this.manuallyTrackedOptionHistory.push(clone(this.manuallyTrackedOptions));
        this.context.save();
        return this;
    }

    public restore(): this {
        if (!this.manuallyTrackedOptionHistory.length) {
            throw new Error('Restore cannot be called more times than save');
        }
        this.context.restore();
        this.manuallyTrackedOptions = this.manuallyTrackedOptionHistory.pop()!;
        this.applyManuallyTrackedOptions();
        return this;
    }

    public runInTransaction(render: (context: CanvasRenderingContext2D) => void): this {
        runInContextTransaction(this.context, render);
        return this;
    }

    // Transformations
    public translate(offset: IPoint): this {
        this.context.translate(offset.x, offset.y);
        return this;
    }

    public scale(factor: IPoint): this {
        this.context.scale(factor.x, factor.y);
        return this;
    }

    public rotate(radians: number): this {
        this.context.rotate(radians);
        return this;
    }

    // eslint-disable-next-line max-params,id-length
    public transform(a: number, b: number, c: number, d: number, e: number, f: number): this {
        this.context.transform(a, b, c, d, e, f);
        return this;
    }

    public opacity(opacity?: number): any {
        if (opacity === undefined) {
            return this.context.globalAlpha;
        } else {
            this.context.globalAlpha = opacity;
            return this;
        }
    }

    // Creating Paths
    public beginPath(): this {
        this.context.beginPath();
        return this;
    }

    public moveTo(point: IPoint): this {
        this.context.moveTo(point.x, point.y);
        return this;
    }

    public lineTo(point: IPoint): this {
        this.context.lineTo(point.x, point.y);
        return this;
    }

    public drawLine(...points: IPoint[]): this {
        drawLine(this, ...points);
        return this;
    }

    public rect(rect: IRectangle): this {
        this.context.rect(rect.x, rect.y, rect.width, rect.height);
        return this;
    }

    public ellipse(ellipse: IEllipse): this {
        this.context.ellipse(
            ellipse.x,
            ellipse.y,
            ellipse.radiusX,
            ellipse.radiusY,
            ellipse.rotation,
            ellipse.startAngle,
            ellipse.endAngle,
            ellipse.anticlockwise,
        );
        return this;
    }

    // Rendering
    public clip(): this {
        this.context.clip();
        return this;
    }

    public fillColor(color?: RenderContextColor): this {
        if (color) {
            this.context.fillStyle = this.colorToRgbHex(color);
        }
        return this;
    }

    public strokeColor(color?: RenderContextColor): this {
        if (color) {
            this.context.strokeStyle = this.colorToRgbHex(color);
        }
        return this;
    }

    public setLineDash(segments: number[]): this {
        this.context.setLineDash(segments);
        return this;
    }

    public fill(color?: RenderContextColor): this {
        if (color) {
            this.context.fillStyle = this.colorToRgbHex(color);
        }
        this.context.fill();
        return this;
    }

    public stroke(color?: RenderContextColor): this {
        if (color) {
            this.context.strokeStyle = this.colorToRgbHex(color);
        }
        this.context.stroke();
        return this;
    }

    public fillAndStroke(fill?: RenderContextColor, stroke?: RenderContextColor): this {
        this.fill(fill);
        this.stroke(stroke);
        return this;
    }

    public lineWidth(): Pixels;

    public lineWidth(width: Pixels): this;

    public lineWidth(width?: Pixels): any {
        if (width === undefined) {
            return this.context.lineWidth;
        }

        this.context.lineWidth = width;
        return this;
    }

    public lineCap(cap: LineCap): this {
        this.context.lineCap = cap;
        return this;
    }

    public lineDash(dashLength: Pixels, gapLength: Pixels): this {
        this.context.setLineDash([dashLength, gapLength]);
        return this;
    }

    public lineDashOffset(offset: Pixels): this {
        this.context.lineDashOffset = offset;
        return this;
    }

    public compositeOperation(operation: BlendMode): this {
        this.context.globalCompositeOperation = operation as GlobalCompositeOperation;
        return this;
    }

    public image(
        source: HTMLCanvasElement | HTMLImageElement | HTMLVideoElement | ImageBitmap,
        point: IPoint,
        options: IImageOptions,
    ): this {
        this.applyManuallyTrackedOptions();

        if (options.width && options.height) {
            this.context.drawImage(source, point.x, point.y, options.width, options.height);
        } else {
            this.context.drawImage(source, point.x, point.y);
        }

        return this;
    }

    // Text Rendering
    public text(options: IRenderContextTextOptions): this {
        this.enforceStandardTextStyles();
        if (options.fill) {
            this.context.fillText(options.text, options.position.x, options.position.y);
        }
        if (options.stroke) {
            this.context.strokeText(options.text, options.position.x, options.position.y);
        }
        return this;
    }

    public textHeight(text: string): number {
        /**
         * This is a hack to calculate the text height based on a StackOverflow
         * thread: https://stackoverflow.com/a/9847841
         */

        this.enforceStandardTextStyles();
        const { height } = this.textMeasurer.measureTextDimensions(
            text,
            this.manuallyTrackedOptions.fontFamily,
            this.manuallyTrackedOptions.fontSize,
            this.manuallyTrackedOptions.fontUnits,
        );
        return height;
    }

    public lineHeight(): number {
        const { fontFamily, fontSize, fontUnits } = this.manuallyTrackedOptions;
        const metrics = findMatchingFontMetricsOrDefault(fontFamily, this.customFonts);
        return calculateLineHeightInPixels({ family: fontFamily, size: fontSize, sizeUnit: fontUnits }, metrics);
    }

    public textWidth(text: string): number {
        this.enforceStandardTextStyles();
        return this.context.measureText(text).width;
    }

    public fontFamily(family: string): this {
        this.manuallyTrackedOptions.fontFamily = family;
        this.applyManuallyTrackedOptions();
        return this;
    }

    public fontWeight(weight: CanvasFontWeight): this {
        this.manuallyTrackedOptions.fontWeight = weight;
        this.applyManuallyTrackedOptions();
        return this;
    }

    public fontSize(size: number, units: FontUnits): this {
        this.manuallyTrackedOptions.fontSize = size;
        this.manuallyTrackedOptions.fontUnits = units;
        this.applyManuallyTrackedOptions();
        return this;
    }

    public letterSpacing(_letterSpacing: number): this {
        /*
         * Letter spacing property is not used by the canvas while rendering.
         * The letter spacing is handle by having Unicode hair spaces between characters.
         * Refer to changes made for DS-4347 for more info.
         */
        return this;
    }

    private enforceStandardTextStyles() {
        this.context.textBaseline = this.baseline;
        this.context.textAlign = 'left';
    }

    private colorToRgbHex(color: string) {
        const rgb = convertToScheme(color, 'rgb')!;

        const result = colorSchemeHelpers.rgb.toString(rgb, 'rgba');

        return result;
    }

    // State Stack
    private applyManuallyTrackedOptions() {
        this.context.font = canvasFontToString({
            family: this.manuallyTrackedOptions.fontFamily,
            size: this.manuallyTrackedOptions.fontSize,
            sizeUnit: this.manuallyTrackedOptions.fontUnits,
            weight: this.manuallyTrackedOptions.fontWeight,
        });
    }
}
